//ANY CHANGES to this file must be replicated in the API 'helpers/validation' library. Must not require any dependencies
import {
  ensureArray,
  formatDateToISO,
  isBefore,
  isFunction,
  lowerCase,
  numberToTime,
  one,
  startCase,
  timeToNumber,
} from "../../../utils/validation";

//Custom input types
const log = (obj: any, ...args: any[]) => console.info(obj, ...args);

export type BasicShift = {
  startTime?: string;
  endTime?: string;
  units?: number;
};
export type BasicEmployeeShift = BasicShift & {
  employeeId: number;
  employeeShiftId?: number;
  shiftPreferenceId?: number;
  resourceGroupId: number;
  date: Date;
  companyId?: number;
};

export const getShiftUnits = (shift: BasicShift, unitOfResource: string) => {
  return unitOfResource === "shifts" ? 1 : shift.units;
};

export const validateAccountNumber = (value: string) =>
  !value ||
  (value.toString().length === 8 && /^\d{8}$/.test(value)) ||
  "Account number must be 8 digits without spaces";
export const validateSortCode = (value: string) =>
  !value ||
  (value.toString().length === 6 && /^\d{6}$/.test(value)) ||
  "Sort code must be 6 digits without spaces";
export const validateIban = (value: string) =>
  !value ||
  /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(value.replace(/\s+/g, "")) ||
  "Iban must be 24 characters without spaces";
export const validateIbanExtended = (value: string) => {
  if (!value) return true;
  const sanitized = value.replace(/\s+/g, "").toUpperCase();
  const rearranged = sanitized.slice(4) + sanitized.slice(0, 4);
  const converted = rearranged.replace(/[A-Z]/g, (ch) =>
    (ch.charCodeAt(0) - 55).toString(),
  );
  const remainder = BigInt(converted) % 97n;
  return remainder === 1n || "Invalid IBAN";
};
export const validateBic = (value: string) =>
  value &&
  (/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(value.toUpperCase()) ||
    "Invalid BIC");
//Parsers

export const parseValueForComparison = (v: unknown): string => {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v.toLowerCase();
  if (typeof v === "object")
    return Object.values(v)
      .map(parseValueForComparison)
      .join(" ")
      .toLowerCase();
  if (Array.isArray(v))
    return v.map(parseValueForComparison).join(" ").toLowerCase();
  if (v instanceof Date) return parseValueForComparison(v.toISOString());
  return parseValueForComparison(v.toString());
};

const isStringNumber = (n: string | number) =>
  typeof n === "string" && n !== "" && !isNaN((n as unknown as number) / 1);
const getRuleValueParser = (
  dataType?: string,
  ruleType = "",
  aggregation: any = null,
) => {
  //ensure the rule value is an array if..
  return getValueParser(
    dataType,
    ruleType,
    !!aggregation || ["in", "!in", "allin", "!allin", "between", "!between"],
  );
};
const getInputValueParser = (dataType: any = null, ruleType = "") => {
  //ensure the input value is an array if..
  return getValueParser(dataType, ruleType);
};
export const aggregate = (aggregation: string, values: number[]) => {
  if (aggregation === "sum") {
    return values.reduce((acc, curr) => acc + curr, 0);
  }
  if (aggregation === "avg") {
    return values.reduce((acc, curr) => acc + curr, 0) / values.length;
  }
  if (aggregation === "min") {
    return Math.min(...values);
  }
  if (aggregation === "max") {
    return Math.max(...values);
  }
  if (aggregation === "count") {
    return values.length;
  }
  if (aggregation === "countDistinct") {
    return new Set(values).size;
  }
  return values.length;
};
export const getValueParser = <Tinput = unknown, Toutput = unknown>(
  dataType: string | null = null,
  ruleType = "",
  arrayIf?: boolean | string[],
): ((v: Tinput) => Toutput) => {
  const basicParse = (v: Tinput) => getValueParser()(v);
  // console.log("getting value parser", dataType, ruleType, arrayIf);
  switch (dataType) {
    case "time":
      if (ruleType.includes("last") || ruleType.includes("next")) {
        return (v) => getValueParser("number", ruleType, arrayIf)(v) as Toutput;
      } else {
        return (v) =>
          (Array.isArray(v) ? v.map(timeToNumber) : timeToNumber(v)) as Toutput;
      }
    case "range":
      return (v: any) =>
        [getValueParser()(v[0] || 0), getValueParser()(v[1] || 0)] as Toutput;
    default:
      return (v) => {
        if (
          arrayIf === true ||
          (Array.isArray(arrayIf) && arrayIf.includes(ruleType))
        ) {
          const parseArray = (arr: any[]) => arr.map((p) => basicParse(p));
          return (
            Array.isArray(v)
              ? parseArray(v)
              : v === undefined || v === null
                ? basicParse(v)
                : parseArray([basicParse(v)])
          ) as Toutput;
        } else {
          return (isStringNumber(v as string) ? Number(v) : v) as Toutput;
        }
      };
  }
};

//comparison

export const stringComparison = (
  v: any,
  o: any,
  includes?: boolean,
): boolean => {
  const val1 = parseValueForComparison(v);
  const val2 = o?.toString().toLowerCase();
  // console.log('doing string comparison', val1, val2);
  return v !== undefined && v !== null && o !== undefined && o !== null
    ? includes
      ? val1.includes(val2)
      : val1 === val2
    : false;
};
const isDateInLast = (date: Date, inLastMs: number) => {
  const now = new Date();
  const then = new Date(date);
  return (
    then.getTime() > now.getTime() - inLastMs && then.getTime() <= now.getTime()
  );
};
const isDateInNext = (date: Date, inNextMs: number) => {
  const now = new Date();
  const then = new Date(date);
  return (
    then.getTime() < now.getTime() + inNextMs && then.getTime() >= now.getTime()
  );
};

const validationFunctions = (type: string): ((a: any, b?: any) => boolean) => {
  switch (type) {
    case "onDay":
      return (a, b) => formatDateToISO(a) === formatDateToISO(b);
    case "empty":
      return (a) => a === "" || a === undefined || a === null || a.length === 0;
    case "!empty":
      return (a) => a !== "" && a !== undefined && a !== null && a.length !== 0;
    case "<":
      return (a, b) => new Date(a) < new Date(b);
    case ">":
      return (a, b) => new Date(a) > new Date(b);
    case "between":
      return (a, b) =>
        new Date(a) >= new Date(b[0]) && new Date(a) <= new Date(b[1]);
    case "!between":
      return (a, b) =>
        new Date(a) < new Date(b[0]) || new Date(a) > new Date(b[1]);
    case "==":
      return (a, b) => stringComparison(a, b);
    case "!=":
      return (a, b) => !stringComparison(a, b);
    case "!contains":
      return (a, b) => !stringComparison(a, b, true);
    case "contains":
      return (a, b) => stringComparison(a, b, true);
    case "in":
      return (a, b) =>
        (Array.isArray(a) ? a : [a]).some((v) =>
          (Array.isArray(b) ? b : [b]).some((o) => stringComparison(v, o)),
        );
    case "!in":
      return (a, b) =>
        !(Array.isArray(a) ? a : [a]).some((v) =>
          (Array.isArray(b) ? b : [b]).some((o) => stringComparison(v, o)),
        );
    case "has":
      return (a, b) =>
        ensureArray(JSON.parse(a || "[]")).some((v) =>
          (Array.isArray(b) ? b : [b]).some((o) => stringComparison(v, o)),
        );
    case "!has":
      return (a, b) =>
        !ensureArray(JSON.parse(a || "[]")).some((v) =>
          (Array.isArray(b) ? b : [b]).some((o) => stringComparison(v, o)),
        );
    case "!allin":
      return (a, b) =>
        !(Array.isArray(b) ? b : [b]).every((v) =>
          (Array.isArray(a) ? a : [a]).some((o) => stringComparison(v, o)),
        );
    case "allin":
      return (a, b) => {
        const res = (Array.isArray(b) ? b : [b]).every((v) =>
          (Array.isArray(a) ? a : [a]).some((o) => stringComparison(v, o)),
        );
        // console.log('comparing allin', a, b, res);
        return res;
      };
    case ">count":
      return (a, b) => (Array.isArray(a) ? a : [a]).length > (b || 0);
    case "<count":
      return (a, b) => (Array.isArray(a) ? a : [a]).length < (b || 0);
    case "isPast":
      return (v, b) => new Date(v) < new Date();
    case "isFuture":
      return (v, b) => new Date(v) > new Date();
    case "lastYears":
      return (v, b) => isDateInLast(v, b * one("year"));
    case "lastDays":
      return (v, b) => isDateInLast(v, b * one("day"));
    case "lastHours":
      return (v, b) => isDateInLast(v, b * one("hour"));
    case "lastMinutes":
      return (v, b) => !isDateInLast(v, b * one("minute"));
    case "!lastYears":
      return (v, b) => !isDateInLast(v, b * one("year"));
    case "!lastDays":
      return (v, b) => !isDateInLast(v, b * one("day"));
    case "!lastHours":
      return (v, b) => !isDateInLast(v, b * one("hour"));
    case "!lastMinutes":
      return (v, b) => !isDateInLast(v, b * one("minute"));
    case "nextYears":
      return (v, b) => isDateInNext(v, b * one("year"));
    case "nextDays":
      return (v, b) => isDateInNext(v, b * one("day"));
    case "nextHours":
      return (v, b) => isDateInNext(v, b * one("hour"));
    case "nextMinutes":
      return (v, b) => isDateInNext(v, b * one("minute"));
    case "!nextYears":
      return (v, b) => !isDateInNext(v, b * one("year"));
    case "!nextDays":
      return (v, b) => !isDateInNext(v, b * one("day"));
    case "!nextHours":
      return (v, b) => !isDateInNext(v, b * one("hour"));
    case "!nextMinutes":
      return (v, b) => !isDateInNext(v, b * one("minute"));

    default:
      return (a, b) => stringComparison(a, b, true);
  }
};

export const getRuleFunc = (
  type: string,
  dataType: string,
  aggregation?: { type: string; column: string } | false,
):
  | ((value: any) => boolean)
  | ((value: any, compareValue?: any) => boolean) => {
  const validator = validationFunctions(type);
  if (aggregation) {
    return (a, b) => {
      const val = a.map((v: any) => v[aggregation.column]);
      return validator(aggregate(aggregation.type, val), b);
    };
  }
  if (type.includes(".")) {
    const parser = getInputValueParser(dataType, type.split(".")[0]);
    return (a, b) => {
      const val = a?.[type.split(".")[1]];
      // console.log(
      //   { subType: type.split('.')[0], value: parser(val) },
      //   'Running rule validation on subtype'
      // );
      return getRuleFunc(type.split(".")[0], dataType)(parser(val), b);
    };
  }
  return (a, b) => {
    //put global overrides/checks
    // console.log('checking rule', { type, a, b, validator });
    if (a === undefined)
      //if no value provided/no answer given, return true only if negative type supplied
      return type === "empty" || type.startsWith("<") || type.startsWith("!");
    //run type-specific validation
    return validator(a, b);
  };
};
export const mutateCustomRule = (
  filters: CustomRule,
  filterer: (f: CustomRule[number][number]) => boolean,
  mutator: (f: CustomRule[number][number]) => CustomRule[number][number],
) => {
  return filters?.reduce((acc, andRule) => {
    const filteredAndRule = andRule.filter(filterer);
    if (filteredAndRule.length > 0) {
      acc.push(filteredAndRule.map(mutator));
    }
    return acc;
  }, [] as CustomRule);
};
export type CustomOrRule<
  T = any,
  TField extends keyof T & string = keyof T & string,
  TValue extends T[TField] = T[TField],
> = {
  type: string;
  value: TValue | TValue[];
  inputType?: string;
  question: TField;
  result?: true | string;
  aggregation?: "count" | "sum" | "avg" | "min" | "max" | "countDistinct";
  //do not type this. It causes the build to crash for some reason
  column?: any;
};
export type CustomAndRule<T = any> = CustomOrRule<T>[];
/** Outer array is ANDed together, inner array is ORed */
export type CustomRule<T = any> = CustomAndRule<T>[];
const isRuleValid = (rule: CustomRule) =>
  rule?.some((f) => f?.some((f) => f?.question));
export const removeRules = (
  rules: CustomRule,
  ...rulesToRemove: CustomRule[]
) => rules?.filter((r) => !stringComparison(r, rulesToRemove));
export const mergeRules = <TData = any>(
  rules: CustomRule<TData>,
  ...rulesToMerge: CustomRule<TData>[]
) => {
  if (!rulesToMerge?.filter(isRuleValid)?.length) return rules || [];
  if (!isRuleValid(rules))
    return rulesToMerge?.filter(isRuleValid).flat() || [];
  return rules.concat(...rulesToMerge.filter(isRuleValid));
};
export const getValidationMessage = (
  dataType: string,
  comparitor: string,
  value: any,
): string => {
  let val = value;
  if (!value) return "";
  if (comparitor.includes(".")) {
    return (
      startCase(comparitor.split(".")[1]) +
      " " +
      getValidationMessage(dataType, comparitor.split(".")[0], val)
    );
  }
  if (!comparitor.includes("last")) {
    switch (dataType) {
      case "time":
        val = numberToTime(val);
        break;
      case "date":
      case "datetime":
        if (Array.isArray(value)) {
          val = value.map((v) => new Date(v).toLocaleDateString("en-GB"));
        } else {
          val = new Date(value).toLocaleDateString("en-GB");
        }
        break;
      default:
        null;
    }
  }
  const getBaseComparitorMessage = () =>
    getValidationMessage("number", comparitor.slice(0, 1), val);
  if (comparitor.includes("shiftDays")) {
    return "Number of days " + lowerCase(getBaseComparitorMessage());
  }
  if (comparitor.includes("shiftMinHours")) {
    return "Minimum possible hours " + lowerCase(getBaseComparitorMessage());
  }
  if (comparitor.includes("shiftMaxHours")) {
    return "Maximum possible hours " + lowerCase(getBaseComparitorMessage());
  }
  const getDateWithinMessage = (span: string) =>
    (comparitor.startsWith("!") ? "Cannot" : "Must") +
    " be within the last " +
    val +
    " " +
    span +
    (val > 1 ? "s" : "") +
    ".";
  if (comparitor.includes("lastYears")) {
    return getDateWithinMessage("year");
  }
  if (comparitor.includes("lastDays")) {
    return getDateWithinMessage("day");
  }
  if (comparitor.includes("lastHours")) {
    return getDateWithinMessage("hour");
  }
  if (comparitor.includes("lastMinutes")) {
    return getDateWithinMessage("minute");
  }
  try {
    switch (comparitor) {
      case "empty":
        return "Must be empty";
      case "!empty":
        return "Cannot be empty";
      case "<":
        return (
          "Must be " +
          (dataType === "date" || dataType === "datetime" || dataType === "time"
            ? "before "
            : "less than ") +
          val
        );
      case ">":
        return (
          "Must be " +
          (dataType === "date" || dataType === "datetime" || dataType === "time"
            ? "after "
            : "more than ") +
          val
        );
      case "between":
        return "Must be between " + val[0] + " and " + val[1];
      case "!between":
        return "Cannot be between " + val[0] + " and " + val[1];
      case "==":
        return "Must equal " + val;
      case "!=":
        return "Cannot equal " + val;
      case "!contains":
        return "Cannot contain '" + value + "'";
      case "contains":
        return "Must contain '" + value + "'";
      case "in":
        return "Must be one of " + value.join(", ");
      case "!in":
        return "Cannot be one of " + value.join(", ");
      case "allin":
        return "Must have all of " + value.join(", ");
      case "!allin":
        return "Cannot have all of " + value.join(", ");
      default:
        return (
          "Value does not satisfy the rule '" + comparitor + " " + value + "'"
        );
    }
  } catch (e) {
    console.error(e, dataType, comparitor, value);
    return "Value is not valid";
  }
};
type CompiledValidateRule = {
  ruleValue: any;
  evaluate: (
    /**must be unparsed */
    inputValue: any,
  ) => true | string;
};
/**
 * Compile once per orRule + inputType.
 * Returns a fast function that can be run for each row.
 */
const compileValidateRule = (
  orRule: CustomOrRule,
  /**must be unparsed */
  inputType: string,
  errorOnPass?: boolean,
): CompiledValidateRule => {
  const evaluate = (value: any) => (errorOnPass ? !value : !!value);

  // ✅ do this once (was per row)
  const inputParser = getInputValueParser(
    orRule.inputType || inputType,
    orRule.type,
  );

  const ruleParser = getRuleValueParser(
    orRule.inputType || inputType,
    orRule.type.split(".")[0],
    !!orRule.aggregation,
  );

  const parsedRuleValue = ruleParser(orRule.value);

  const ruleFunc = getRuleFunc(
    orRule.type,
    inputType,
    !!orRule.aggregation && {
      type: orRule.aggregation,
      column: orRule.question.split(".")[1],
    },
  );

  const failMessage = getValidationMessage(
    orRule.inputType || inputType,
    orRule.type,
    parsedRuleValue,
  );

  // ✅ hot path: only parse the input and run the function
  return {
    ruleValue: parsedRuleValue,
    evaluate: (inputValue: any) => {
      const result = ruleFunc(inputParser(inputValue), parsedRuleValue);
      return evaluate(result) === true ? true : failMessage;
    },
  };
};

/**
 * By default, will return true if the rule passes, and an error message if the rule has failed. @param errorOnPass If true, will return an error message if the rule passes
 */
const validateRule = (
  orRule: CustomOrRule,
  /**must be unparsed */
  inputType: string,
  /**must be unparsed */
  inputValue: any,
  errorOnPass?: boolean,
): true | string => {
  const evaluate = (value: any) => (errorOnPass ? !value : !!value);
  const inputParser = getInputValueParser(
    orRule.inputType || inputType,
    orRule.type,
  );
  const ruleParser = getRuleValueParser(
    orRule.inputType || inputType,
    //the rule value will always just be the value, not an object type like the input value, so needs to be parsed directly in all case
    orRule.type.split(".")[0],
    !!orRule.aggregation,
  );
  const ruleFunc = getRuleFunc(
    orRule.type,
    inputType,
    !!orRule.aggregation && {
      type: orRule.aggregation,
      column: orRule.question.split(".")[1],
    },
  );
  const result = ruleFunc(inputParser(inputValue), ruleParser(orRule.value));
  // log(
  //   {
  //     orRule,
  //     result,
  //     rawInput: inputValue,
  //     inputValue: inputParser(inputValue),
  //     ruleValue: ruleParser(orRule.value)
  //   },
  //   'Validating rule value'
  // );
  return evaluate(result)
    ? true
    : getValidationMessage(
        orRule.inputType || inputType,
        orRule.type,
        ruleParser(orRule.value),
      );
};
export type ValidationResult = {
  rule: CustomOrRule;
  value: any;
  inputType: string;
  result: string | true;
  description: string;
};
export type ValidationRuleResults = ValidationResult[][];
type PropertyGetter<TReturn> =
  | ((rule?: CustomOrRule) => TReturn)
  | (TReturn extends Function ? (rule: CustomOrRule) => TReturn : never);
const getProperty = <TReturn = any>(
  getter: PropertyGetter<TReturn>,
  rule: CustomOrRule,
) =>
  isFunction(getter) ? getter(rule) : (getter as Exclude<TReturn, Function>);
const mapValidationResults = (
  rules: CustomRule,
  inputType: PropertyGetter<string>,
  inputValue: PropertyGetter<any>,
  errorOnPass?: boolean,
  enabled: PropertyGetter<boolean> = () => true,
): ValidationRuleResults => {
  return rules.map((andRule) => {
    return (Array.isArray(andRule) ? andRule : !andRule ? [] : [andRule]).map(
      (rule) => {
        const _inputType = getProperty(inputType, rule);
        const _inputValue = getProperty(inputValue, rule);

        const compiled = compileValidateRule(rule, _inputType, errorOnPass);
        const description = getValidationMessage(
          _inputType,
          rule.type,
          compiled.ruleValue,
        );
        return {
          rule,
          value: _inputValue,
          inputType: _inputType,
          description,
          result: !getProperty(enabled, rule)
            ? true
            : compiled.evaluate(_inputValue),
        };
      },
    );
  });
};

const areAllResultsTrue = (results: ValidationRuleResults) =>
  results.every((orRules) => orRules.some((rule) => rule.result === true));
const areAnyResultsTrue = (results: ValidationRuleResults) =>
  results.some((orRules) => orRules.some((rule) => rule.result === true));
const combineErrors = (results: ValidationRuleResults) =>
  results
    ?.map((orRules) =>
      orRules
        .map((rule) => (typeof rule.result === "string" ? rule.result : null))
        .filter((r) => r)
        .join(" or "),
    )
    .join(" and ") || "";

/**
 * Returns true if all rules pass. A combined single error message stating why they failed if not
 */
const trueIfAllPass = (
  rules: CustomRule,
  inputType: PropertyGetter<string>,
  inputValue: PropertyGetter<string | Array<any> | object | boolean | number>,
  enabled: PropertyGetter<boolean> = () => true,
) => {
  const results = mapValidationResults(
    rules,
    inputType,
    inputValue,
    false,
    enabled,
  );
  const haveAllPassed = areAllResultsTrue(results);
  if (haveAllPassed) return true;
  const combinedError = combineErrors(results);
  return combinedError;
};

/**
 * Returns true if any rules pass. A combined single error message stating why they failed if not
 */
const trueIfAnyPass = (
  rules: CustomRule,
  inputType: PropertyGetter<string>,
  inputValue: PropertyGetter<any>,
  enabled: PropertyGetter<boolean> = () => true,
) => {
  const results = mapValidationResults(
    rules,
    inputType,
    inputValue,
    false,
    enabled,
  );
  const haveAnyPassed = areAnyResultsTrue(results);
  if (haveAnyPassed) return true;
  const combinedError = combineErrors(results);
  return combinedError;
};

/**
 * Returns true if all rules fail. A combined single error message stating why they passed if not
 */
const trueIfAllFail = (
  rules: CustomRule,
  inputType: PropertyGetter<string>,
  inputValue: PropertyGetter<any>,
  enabled: PropertyGetter<boolean> = () => true,
) => {
  const results = mapValidationResults(
    rules,
    inputType,
    inputValue,
    true,
    enabled,
  );
  const haveAllFailed = areAllResultsTrue(results);
  if (haveAllFailed) return true;
  const combinedError = combineErrors(results);
  return combinedError;
};

/**
 * Returns true if any rules fail. A combined single error message stating why they passed if not
 */
const trueIfAnyFail = (
  rules: CustomRule,
  inputType: PropertyGetter<string>,
  inputValue: PropertyGetter<any>,
  enabled: PropertyGetter<boolean> = () => true,
) => {
  const results = mapValidationResults(
    rules,
    inputType,
    inputValue,
    true,
    enabled,
  );
  const haveAnyFailed = areAllResultsTrue(results);
  if (haveAnyFailed) return true;
  const combinedError = combineErrors(results);
  return combinedError;
};

export const validateCustomRule = (rule: CustomRule, data: any) => {
  const res = validate.trueIfAllPass(
    rule,
    (d) => d?.inputType as string,
    (r) => (r ? data[r.question] : null),
  );
  return res === true;
};
export const validate = {
  trueIfAllFail,
  trueIfAllPass,
  trueIfAnyFail,
  trueIfAnyPass,
  validateRule,
  mapResults: mapValidationResults,
  combineErrors,
};
export const endDateValidator = (
  value: Date | string,
  data: { startDate?: Date | string },
) => {
  if (!value || !data?.startDate) return;
  if (isBefore(value, data.startDate))
    return "End date must be after start date";
};
export const maxValidator = (number: number) => {
  if (!number) return;
  return {
    value: number,
    message: `Cannot be more than ${number}`,
  };
};
export const minValidator = (number: number) => {
  return {
    value: number,
    message: `Must be at least ${number}`,
  };
};
